/**
 * `/oms/quotas` — bulk quota-adjustment + per-customer overrides
 * (OCP-137).
 *
 * Reuses the customers list endpoint (`/v1/admin/oms/customers`) for
 * the table — OMS-18 spec keeps quota inline on the customer rows.
 * Per-row edit + bulk-adjust both fan out to PATCH calls (no bulk
 * endpoint on OMS side).
 *
 * Three actions:
 *   - Edit one customer's quota (modal)
 *   - Bulk-adjust selected (set base_quota, allow_overage, cap %)
 *   - Tier-template-apply: stub for now — full templates need a
 *     portal_products lookup that doesn't ship in this slice
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';

import { api, ApiError } from '../../lib/api';
import { UnconfiguredEmptyState, formatDate } from './CustomerDetailPage';

interface QuotaCustomer {
  id: string;
  email: string;
  plan_tier?: string | null;
  status?: string | null;
  created_at?: string | null;
  quota?: {
    base_quota?: number;
    used?: number;
    allow_overage?: boolean;
    overage_cap_percent?: number;
  } | null;
}

interface CustomersResponse {
  customers: QuotaCustomer[];
  cursor?: string | null;
}

interface QuotaPatch {
  base_quota?: number;
  allow_overage?: boolean;
  overage_cap_percent?: number;
}

export function OmsQuotasPage() {
  const [search, setSearch] = useState('');
  const [data, setData] = useState<CustomersResponse | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error' | 'unconfigured'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<QuotaCustomer | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(
    null,
  );

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    p.set('limit', '100');
    return p.toString();
  }, [search]);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await api<CustomersResponse>(`/v1/admin/oms/customers?${qs}`);
      setData(r);
      setSelected(new Set());
      setState('idle');
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setState('unconfigured');
        return;
      }
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setErrorDetail(body?.detail ?? body?.error ?? `HTTP ${err.status}`);
      } else {
        setErrorDetail(err instanceof Error ? err.message : 'Unknown');
      }
      setState('error');
    }
  }, [qs]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patchOne(id: string, patch: QuotaPatch): Promise<boolean> {
    setActionError(null);
    try {
      await api(`/v1/admin/oms/customers/${id}/quota`, { method: 'PATCH', body: patch });
      return true;
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setActionError(body?.detail ?? body?.error ?? `HTTP ${err.status}`);
      } else {
        setActionError(err instanceof Error ? err.message : 'Patch failed');
      }
      return false;
    }
  }

  async function bulkPatch(patch: QuotaPatch) {
    const ids = Array.from(selected);
    setBulkProgress({ done: 0, total: ids.length });
    for (let i = 0; i < ids.length; i++) {
      await patchOne(ids[i]!, patch);
      setBulkProgress({ done: i + 1, total: ids.length });
    }
    setBulkProgress(null);
    setBulkOpen(false);
    await load();
  }

  if (state === 'unconfigured') return <UnconfiguredEmptyState />;

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">orboto Mail · Quotas</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Bulk + per-customer quota adjustments. Bulk-edit fans out to per-customer
          PATCH calls (no bulk endpoint on the orboto Mail Service side).
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Search customer</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            disabled={bulkProgress !== null}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:bg-gray-400"
          >
            {bulkProgress
              ? `Adjusting ${bulkProgress.done}/${bulkProgress.total}…`
              : `Bulk-adjust ${selected.size} customers`}
          </button>
        )}
      </div>

      {actionError && (
        <div className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {actionError}
        </div>
      )}
      {state === 'error' && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4">
          <h2 className="text-sm font-semibold text-red-900 dark:text-red-200">Could not load quotas</h2>
          {errorDetail && (
            <p className="mt-1 break-all text-xs text-red-800 dark:text-red-300">{errorDetail}</p>
          )}
        </div>
      )}

      {data && data.customers.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={
                      data.customers.length > 0 &&
                      selected.size === data.customers.length
                    }
                    onChange={() =>
                      setSelected((prev) =>
                        prev.size === data.customers.length
                          ? new Set()
                          : new Set(data.customers.map((c) => c.id)),
                      )
                    }
                    className="h-4 w-4 accent-brand-500"
                  />
                </th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Plan</th>
                <th className="px-3 py-2">Base</th>
                <th className="px-3 py-2">Used</th>
                <th className="px-3 py-2">Overage</th>
                <th className="px-3 py-2">Cap %</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.customers.map((c) => (
                <tr key={c.id} className={selected.has(c.id) ? 'bg-brand-50/50' : ''}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${c.email}`}
                      checked={selected.has(c.id)}
                      onChange={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.id)) next.delete(c.id);
                          else next.add(c.id);
                          return next;
                        })
                      }
                      className="h-4 w-4 accent-brand-500"
                    />
                  </td>
                  <td className="px-3 py-2 font-medium">{c.email}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{c.plan_tier ?? '—'}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {c.quota?.base_quota?.toLocaleString() ?? '—'}
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {c.quota?.used?.toLocaleString() ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    {c.quota?.allow_overage ? 'yes' : 'no'}
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {c.quota?.overage_cap_percent ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {c.created_at ? formatDate(c.created_at) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setEditTarget(c)}
                      className="text-xs text-brand-500 hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        state === 'idle' && (
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
            No customers match.
          </div>
        )
      )}

      {editTarget && (
        <QuotaModal
          title={`Adjust quota — ${editTarget.email}`}
          initial={editTarget.quota ?? null}
          onCancel={() => setEditTarget(null)}
          onSubmit={async (patch) => {
            const ok = await patchOne(editTarget.id, patch);
            if (ok) {
              setEditTarget(null);
              await load();
            }
          }}
        />
      )}
      {bulkOpen && (
        <QuotaModal
          title={`Bulk-adjust ${selected.size} customers`}
          initial={null}
          onCancel={() => setBulkOpen(false)}
          onSubmit={(patch) => bulkPatch(patch)}
        />
      )}
    </div>
  );
}

function QuotaModal({
  title,
  initial,
  onCancel,
  onSubmit,
}: {
  title: string;
  initial: { base_quota?: number; allow_overage?: boolean; overage_cap_percent?: number } | null;
  onCancel: () => void;
  onSubmit: (p: QuotaPatch) => Promise<void> | void;
}) {
  const [base, setBase] = useState(String(initial?.base_quota ?? ''));
  const [overage, setOverage] = useState(Boolean(initial?.allow_overage));
  const [cap, setCap] = useState(String(initial?.overage_cap_percent ?? ''));
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const patch: QuotaPatch = { allow_overage: overage };
      if (base.trim() !== '') patch.base_quota = Number(base);
      if (cap.trim() !== '') patch.overage_cap_percent = Number(cap);
      await onSubmit(patch);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-4 text-base font-semibold">{title}</h2>
        <form onSubmit={submit} className="flex flex-col gap-3 text-sm">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">Base quota</span>
            <input
              type="number"
              min={0}
              value={base}
              onChange={(e) => setBase(e.target.value)}
              placeholder="Leave blank to keep current"
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5"
            />
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={overage}
              onChange={(e) => setOverage(e.target.checked)}
              className="h-4 w-4 accent-brand-500"
            />
            <span>Allow overage</span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">Overage cap %</span>
            <input
              type="number"
              min={0}
              max={1000}
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              disabled={!overage}
              placeholder="Leave blank to keep current"
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 disabled:bg-gray-100 dark:disabled:bg-gray-800/60"
            />
          </label>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:bg-gray-400"
            >
              {submitting ? '…' : 'Apply'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
